import { environment } from 'apps/chatbot-client/src/environments/environment';
import React, { useState, useEffect, useRef } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import styled from 'styled-components';

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.mjs',
  import.meta.url
).toString();

interface PDFViewerProps {
  fileName: string;
  pdfUrl: string;
  pageNumber: number;
  open: boolean;
  onClose: () => void;
}

const Header = styled.div`
  position: sticky;
  top: 0;
  z-index: 10;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 24px 12px 16px;
  border-bottom: 1px solid #e0e0e0;
  min-height: 64px;
`;

const Title = styled.h4`
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
  flex: 1;
  text-align: center;
  color: #222;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CloseButton = styled.button`
  all: unset;
  color: #888;
  font-size: 28px;
  cursor: pointer;
  margin-left: 16px;
  padding: 4px 8px;
  border-radius: 4px;
`;

const PDFViewer: React.FC<PDFViewerProps> = ({ fileName, pdfUrl, pageNumber, open, onClose }) => {
  const [numPages, setNumPages] = useState<number | null>(null);
  const [pagesRendered, setPagesRendered] = useState(0);
  const sasToken = environment.icDocSasToken;
  const pageRefs = useRef<(HTMLDivElement | null)[]>([]);

  function onDocumentLoadSuccess({ numPages }: { numPages: number }) {
    setNumPages(numPages);
    console.log('Number of pages:', numPages + ' and current page number:', pageNumber);
    setPagesRendered(0); // reset when new doc loads
  }

  const onPageRender = () => {
    setPagesRendered(prev => prev + 1);
  };

  // Scroll after all pages are rendered
  useEffect(() => {
    if (
      numPages &&
      pagesRendered === numPages &&
      pageNumber &&
      pageRefs.current[pageNumber - 1]
    ) {
      pageRefs.current[pageNumber - 1]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [numPages, pagesRendered, pageNumber, open]);

  if (!open) return null;

 return (
    <div
      style={{
        position: 'fixed',
        top: 0, left: 0, right: 0, bottom: 0,
        background: 'rgba(0,0,0,0.5)',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      <div
        style={{
          background: '#fff',
          padding: 0,
          borderRadius: 8,
          width: '90vw',
          maxWidth: 900,
          height: '100vh',
          boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
          position: 'relative',
          overflow: 'hidden'
        }}
        onClick={e => e.stopPropagation()}
      >
        <Header>
          <Title title={fileName}>{fileName}</Title>
          <CloseButton onClick={onClose} aria-label="Close">&times;</CloseButton>
        </Header>
        <div style={{ width: '100%', height: 'calc(100vh - 64px)', overflow: 'auto', padding: 16 }}>
          <Document file={pdfUrl + "?" + sasToken} onLoadSuccess={onDocumentLoadSuccess}>
            {numPages &&
              Array.from({ length: numPages }, (_, idx) => (
                <div
                  key={idx}
                  ref={el => {
                    pageRefs.current[idx] = el;
                  }}
                  style={{
                    margin: '10px auto',
                    padding: '10px',
                    border: '1px solid #e0e0e0',
                    borderRadius: 8,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.07)',
                  }}
                >
                  <Page
                    pageNumber={idx + 1}
                    renderAnnotationLayer={false}
                    renderTextLayer={false}
                    width={800}
                    onRenderSuccess={onPageRender}
                  />
                </div>
              ))}
          </Document>
        </div>
      </div>
    </div>
  );
};

export default PDFViewer;